import React from 'react';
import JiraKeyChip from '../JiraKeyChip';
import * as fmt from '../../lib/format';
import { PLAN_STATUS_LABEL, PlanRow, planStatusStyle } from './types';

export function StatusPill({ row }: { row: Pick<PlanRow, 'plan_status' | 'jira_status' | 'sheet_status'> }) {
  const style = planStatusStyle(row.plan_status);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${style.badge}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden />
        {PLAN_STATUS_LABEL[row.plan_status]}
      </span>
      {row.jira_status && <span className="text-[0.6875rem] text-gray-600">{row.jira_status}</span>}
    </span>
  );
}

interface Props {
  /** Names the table for a screen reader: the project, or the section. */
  caption: string;
  rows: PlanRow[];
  /** The id of the row whose panel is open. */
  openId: string | null;
  onOpen: (id: string) => void;
  /** Show the project beside each row (a section that mixes projects). */
  showProject?: boolean;
}

// One project's rows, as the planning sheet showed them. The table scrolls inside its own box with the task pinned,
// so the page itself never scrolls sideways on a phone.
export default function PlanTable({ caption, rows, openId, onOpen, showProject = false }: Props) {
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[44rem] border-separate border-spacing-0 text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
            <th scope="col" className="sticky left-0 z-10 border-b border-gray-200 bg-white px-4 py-2 sm:min-w-[18rem]">Task</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Ticket</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">{showProject ? 'Project' : 'Component'}</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Priority</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Status</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Owner</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Planned completion</th>
            <th scope="col" className="border-b border-gray-200 px-3 py-2">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = r.id === openId;
            const cell = 'border-b border-gray-100 px-3 py-2.5 align-top';
            return (
              <tr key={r.id} className={open ? 'bg-blue-50/60' : 'hover:bg-gray-50'}>
                <th scope="row" className={`sticky left-0 z-10 border-b border-gray-100 px-4 py-2.5 text-left align-top font-normal ${open ? 'bg-blue-50' : 'bg-white'}`}>
                  <button
                    type="button"
                    onClick={() => onOpen(r.id)}
                    aria-haspopup="dialog"
                    className="block w-full max-w-[18rem] break-words rounded text-left text-sm leading-snug text-gray-900 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    {r.title}
                  </button>
                  {r.created_before_boundary && r.plan_status !== 'live' && r.plan_status !== 'dropped' && (
                    <span className="mt-1 block text-[0.6875rem] text-gray-600">Started before the Jira conventions</span>
                  )}
                </th>
                <td className={cell}>{r.issue_key && r.jira_url ? <JiraKeyChip issueKey={r.issue_key} url={r.jira_url} /> : <span className="text-xs text-gray-600">No ticket yet</span>}</td>
                <td className={`${cell} text-gray-700`}>{(showProject ? r.project_name : r.component) ?? <span aria-hidden>–</span>}</td>
                <td className={`${cell} tabular-nums text-gray-700`}>{r.note.priority ?? <span aria-hidden>–</span>}</td>
                <td className={cell}><StatusPill row={r} /></td>
                <td className={`${cell} text-gray-700`}>{r.owner ?? r.requested_by ?? <span aria-hidden>–</span>}</td>
                <td className={`${cell} text-gray-700`}>{r.note.planned_completion ?? <span aria-hidden>–</span>}</td>
                <td className={`${cell} whitespace-nowrap text-gray-700`}>{r.updated_at ? fmt.date(r.updated_at) : <span aria-hidden>–</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
